import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Constants } from '../../helper/constants';
import { Global } from '../../../../app.global';
import { GlobalObservables } from '../../../../app.global.observer';
import { AlertModel } from '../../viewmodels/alert.model';
import { AlertsService } from '../../services/alerts.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'custom-letters',
  templateUrl: './custom-letters.component.html',
  styleUrls: ['./custom-letters.component.scss'],
})
export class CustomLettersComponent implements OnInit, OnDestroy {
  public constants = Constants;
  customLetters: Array<AlertModel> = [];
  originalCustomLetters: Array<AlertModel> = [];
  pagedCustomLetters: Array<AlertModel> = [];
  customLettersPerPage = 10;
  numberOfPages = 1;
  selectedPage = 1;
  pagesList = [1];
  customLettersSort = '';
  tooltipPlacement = 'top';
  private activeInstructionsPopover: NgbPopover | null = null;
  private instructionsPopoverHoverCleanup: (() => void) | null = null;

  constructor(
    public global: Global,
    public globalObs: GlobalObservables,
    private alertsService: AlertsService
  ) {}

  ngOnInit() {
    this.setPagesList();
    this.sortCustomLetters(this.customLettersSort);
    this.getCustomLetters();
    this.pagedCustomLetters = this.customLetters.slice(0, this.customLettersPerPage);
    this.setTooltipPlacement();
  }

  // Close available custom letters
  closeCustomLetter() {
    this.globalObs.onCustomLettersShow.next(false);
  }

  getCustomLetters() {
    this.customLetters = this.global.customLetters;
    this.originalCustomLetters = this.customLetters;
    this.setPagesList();
  }

  setPagesList() {
    this.numberOfPages = Math.ceil(this.customLetters.length / this.customLettersPerPage);
    this.pagesList = [1];
    for (let i = 2; i <= this.numberOfPages; i++) {
      this.pagesList.push(i);
    }
  }

  setTooltipPlacement() {
    if (window.innerWidth <= this.constants.breakpoints.desktopSm) {
      this.tooltipPlacement = 'top';
    }
  }

  // The instructions popover opens on hover and, since it can now scroll, needs to
  // survive the cursor moving off the row and onto it. Three parts work together:
  //  - shrinkInstructionsPopoverGap narrows Popper's default 8px offset to 2px
  //    (the same value ng-bootstrap itself uses for its datepicker/dropdown),
  //    so there's less empty space between row and popover to cross
  //  - closeDelay (template) gives the cursor time to cross what's left of that gap
  //  - onInstructionsPopoverShown attaches hover listeners to the *whole* rendered
  //    popover element (border, padding and scrollbar included, not just the
  //    instructions text) so the bubble persists as long as the cursor is
  //    anywhere over it - including while dragging the scrollbar thumb, which
  //    sits in the popover's padding rather than in the text content itself -
  //    and also force-closes whichever popover was previously open, so two are
  //    never visible together regardless of the delay above
  shrinkInstructionsPopoverGap = (options: any) => ({
    ...options,
    modifiers: [
      ...(options.modifiers ?? []),
      { name: 'offset', options: { offset: () => [0, 2] } },
    ],
  });

  onInstructionsPopoverShown(popover: NgbPopover, triggerEl: HTMLElement) {
    if (this.activeInstructionsPopover && this.activeInstructionsPopover !== popover) {
      this.detachInstructionsPopoverHoverBridge();
      this.activeInstructionsPopover.close();
    }
    this.activeInstructionsPopover = popover;
    this.attachInstructionsPopoverHoverBridge(triggerEl);
  }

  onInstructionsPopoverHidden(popover: NgbPopover) {
    if (this.activeInstructionsPopover === popover) {
      this.detachInstructionsPopoverHoverBridge();
      this.activeInstructionsPopover = null;
    }
  }

  changePage(pageNumber) {
    const startIndex = (pageNumber - 1) * this.customLettersPerPage;
    const endIndex = pageNumber * this.customLettersPerPage;
    this.pagedCustomLetters = this.customLetters.slice(startIndex, endIndex);
    this.selectedPage = pageNumber;
  }

  sortCustomLetters(sortName) {
    if (sortName === 'decision') {
      this.customLetters.sort((obj1, obj2) => {
        if (obj1.decisionName < obj2.decisionName) {
          return -1;
        }
        if (obj1.decisionName > obj2.decisionName) {
          return 1;
        }
        return 0;
      });
    } else if (sortName === 'name') {
      this.customLetters.sort((obj1, obj2) => {
        if (obj1.name < obj2.name) {
          return -1;
        }
        if (obj1.name > obj2.name) {
          return 1;
        }
        return 0;
      });
    }
    this.customLettersSort = sortName;
    this.changePage(this.selectedPage);
  }

  filterCustomLetters(criteria) {
    // Create a case-insensitive regular expression from the criteria
    const regex = new RegExp(criteria, 'i');

    this.customLetters = this.originalCustomLetters.filter((letter) => {
      if (criteria === '') {
        return true;
      }

      // Test if the letter name matches the regular expression
      return regex.test(letter.name);
    });

    this.changePage(1);
    this.sortCustomLetters(this.customLettersSort);
    this.setPagesList();
  }

  existLetterSelected() {
    let bool = false;
    this.customLetters.forEach((letter) => {
      if (letter.selected) {
        bool = true;
      }
    });
    return bool;
  }

  selectCustomLetter(letter) {
    if (this.existLetterSelected() && !letter.selected) {
      return;
    }
    letter.selected = !letter.selected;
    this.alertsService.addRemoveCustomLetter(letter.id, !letter.selected).subscribe({
      next: () => {
        if (this.existLetterSelected()) {
          this.globalObs.addCustomLetter.next(true);
        } else {
          this.globalObs.removeCustomLetter.next(true);
        }
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  ngOnDestroy() {
    this.detachInstructionsPopoverHoverBridge();
    // clear selected custom letters on leaving the component
    this.customLetters.forEach((letter) => {
      letter.selected = false;
    });
  }

  private attachInstructionsPopoverHoverBridge(triggerEl: HTMLElement) {
    this.detachInstructionsPopoverHoverBridge();
    const popoverId = triggerEl.getAttribute('aria-describedby');
    const popoverEl = popoverId ? document.getElementById(popoverId) : null;
    if (!popoverEl) {
      return;
    }
    const keepOpen = () => triggerEl.dispatchEvent(new MouseEvent('mouseenter'));
    const scheduleClose = () => triggerEl.dispatchEvent(new MouseEvent('mouseleave'));
    popoverEl.addEventListener('mouseenter', keepOpen);
    popoverEl.addEventListener('mouseleave', scheduleClose);
    this.instructionsPopoverHoverCleanup = () => {
      popoverEl.removeEventListener('mouseenter', keepOpen);
      popoverEl.removeEventListener('mouseleave', scheduleClose);
    };
  }

  private detachInstructionsPopoverHoverBridge() {
    if (this.instructionsPopoverHoverCleanup) {
      this.instructionsPopoverHoverCleanup();
      this.instructionsPopoverHoverCleanup = null;
    }
  }
}
